import { useParams, Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles, categories } from '@/mocks/data';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find(c => c.slug === slug) || categories[0];
  const filteredCategoryArticles = articles.filter(a => a.categorySlug === slug);
  const categoryArticles = filteredCategoryArticles.length ? filteredCategoryArticles : articles;
  const otherCategories = categories.filter(c => c.slug !== slug).slice(0, 4);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: category.name },
  ];

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 51在线吃瓜网`,
    description: category.description,
    url: 'http://jnjiu.cn/',
    isPartOf: {
      '@type': 'WebSite',
      name: '51在线吃瓜网',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>
      <SEO
        title={`${category.name} - 51在线吃瓜网 | 最新${category.name}爆料资讯`}
        description={category.description}
        keywords={`${category.name},51在线吃瓜,${category.name}爆料,${category.name}最新,吃瓜资讯`}
        canonicalUrl={`/category/${slug}`}
      />
      <Navbar />
      <main className="pt-16">
        {/* Header Banner */}
        <section className="relative bg-foreground-950 py-16 md:py-24">
          <div className="absolute inset-0 overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=dramatic%20dark%20abstract%20texture%20background%20with%20subtle%20red%20and%20warm%20accents%20cinematic%20lighting%20modern%20editorial%20design%20atmosphere%20mysterious%20mood&width=1800&height=400&seq=category-bg-01&orientation=landscape"
              alt=""
              className="w-full h-full object-cover opacity-40"
              aria-hidden="true"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-black/80" />
          </div>
          <div className="relative z-10 max-w-[1440px] mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />
            <div className="mt-6 md:mt-8">
              <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-500/20 mb-4">
                <i className={`${category.icon} text-primary-400 text-xl`} />
              </span>
              <h1 className="font-heading text-3xl md:text-5xl font-black text-white leading-tight mb-3">
                {category.name}
              </h1>
              <p className="text-white/70 text-sm md:text-base max-w-xl font-label">
                {category.description}
              </p>
              <div className="mt-4 flex items-center gap-2">
                <span className="tag-pill bg-white/10 text-white/80 font-label">
                  共 {category.count} 篇内容
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-10 md:py-16 bg-background-50">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              {/* Article List */}
              <div className="lg:col-span-8">
                <div className="flex flex-col gap-5">
                  {categoryArticles.length > 0 ? (
                    categoryArticles.map(article => (
                      <Link
                        key={article.id}
                        to={`/article/${article.slug}`}
                        className="group flex flex-col sm:flex-row bg-white rounded-xl overflow-hidden card-hover border border-background-200/70"
                      >
                        <div className="sm:w-72 md:w-80 flex-shrink-0 aspect-video sm:aspect-[4/3] overflow-hidden">
                          <img
                            src={article.imageUrl}
                            alt={article.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <div className="p-4 md:p-5 flex flex-col justify-between flex-1 min-w-0">
                          <div>
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <span className="tag-pill bg-primary-50 text-primary-700 font-label">
                                {article.category}
                              </span>
                              <span className="text-foreground-400 text-xs font-label">{article.publishedAt}</span>
                            </div>
                            <h2 className="font-heading font-bold text-foreground-950 text-base md:text-lg line-clamp-2 mb-2 group-hover:text-primary-500 transition-colors">
                              {article.title}
                            </h2>
                            <p className="text-foreground-500 text-sm line-clamp-2 font-label hidden sm:block">
                              {article.excerpt}
                            </p>
                          </div>
                          <div className="mt-3 flex items-center gap-4 text-foreground-400 text-xs font-label">
                            <span className="flex items-center gap-1">
                              <i className="ri-fire-line" />
                              {(article.heat / 10000).toFixed(1)}w
                            </span>
                            <span className="flex items-center gap-1">
                              <i className="ri-chat-3-line" />
                              {article.comments} 评论
                            </span>
                          </div>
                        </div>
                      </Link>
                    ))
                  ) : (
                    <div className="text-center py-16 bg-white rounded-xl border border-background-200/70">
                      <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-full bg-background-100">
                        <i className="ri-article-line text-foreground-400 text-xl" />
                      </div>
                      <p className="text-foreground-500 text-sm font-label">该分类暂无文章</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Sidebar */}
              <aside className="lg:col-span-4 space-y-6">
                {/* Related Categories */}
                <div className="bg-white rounded-xl p-5 border border-background-200/70">
                  <h3 className="font-heading font-bold text-foreground-900 text-sm mb-4">其他频道</h3>
                  <div className="flex flex-col gap-2">
                    {otherCategories.map(cat => (
                      <Link
                        key={cat.slug}
                        to={`/category/${cat.slug}`}
                        className="flex items-center gap-3 p-3 rounded-lg hover:bg-background-50 transition-colors group"
                      >
                        <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0">
                          <i className={`${cat.icon} text-foreground-500 text-sm group-hover:text-primary-500 transition-colors`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-foreground-800 text-sm font-label truncate">{cat.name}</p>
                          <p className="text-foreground-400 text-xs font-label">{cat.count} 篇</p>
                        </div>
                        <i className="ri-arrow-right-s-line text-foreground-300 text-sm" />
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Hot Articles */}
                <div className="bg-white rounded-xl p-5 border border-background-200/70">
                  <h3 className="font-heading font-bold text-foreground-900 text-sm mb-4">热门推荐</h3>
                  <div className="flex flex-col gap-3">
                    {articles
                      .filter(a => a.categorySlug !== slug)
                      .sort((a, b) => b.heat - a.heat)
                      .slice(0, 5)
                      .map((article, i) => (
                        <Link
                          key={article.id}
                          to={`/article/${article.slug}`}
                          className="flex items-start gap-3 group"
                        >
                          <span className={`w-5 h-5 flex items-center justify-center rounded-md text-xs font-label font-bold flex-shrink-0 ${
                            i < 3 ? 'bg-primary-500 text-white' : 'bg-background-100 text-foreground-500'
                          }`}>
                            {i + 1}
                          </span>
                          <p className="text-foreground-700 text-sm font-label line-clamp-2 group-hover:text-primary-500 transition-colors">
                            {article.title}
                          </p>
                        </Link>
                      ))}
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}